import React, { useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import LayerFunctional from "../../../LayerFunctional";
import MainContext from "../../../Context/MainContext/MainContext";
import CardAdminWidgetGeneral from "../../../Component/Card/Admin/Widget/CardAdminWidgetGeneral";
import PageHeader from "../../../Component/PageHeader";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import Validator from "../../../Utility/Validator";
import Widget from "../../../Utility/Crud/Widget";
import api from "../../../Seating/Security/api";

export default function AdminWidgetCreateEdit() {
  const context = useContext(MainContext);
  const history = useHistory();
  const { id } = useParams();

  const isUpdate = Boolean(id);
  const widgetId = Number(id);
  const [widget, setWidget] = useState(() =>
    isUpdate ? context.widgetsIndexed[widgetId] : {},
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState({});

  const createWidget = async () => {
    setIsProcessing(true);
    return api
      .send("POST", "/widget", widget)
      .then((createdWidget) => {
        context.updateIndexed("widgetsIndexed", createdWidget);
        history.goBack();
        NotistackEnqueueSnackbar("Widget created successfully!", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error creating a new Widget.",
          { variant: "error" },
        );
        setIsProcessing(false);
      });
  };

  const updateWidget = async () => {
    setIsProcessing(true);
    return api
      .send("PUT", "/widget", widget)
      .then((updatedWidget) => {
        context.updateIndexed("widgetsIndexed", updatedWidget);
        history.goBack();
        NotistackEnqueueSnackbar("Widget updated successfully!", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error updating the Widget.",
          { variant: "error" },
        );
        setIsProcessing(false);
      });
  };

  const deleteWidget = async () => {
    setIsProcessing(true);
    return api
      .send("DELETE", `/widget/${widget.id}`)
      .then(() => {
        context.deleteIndexed("widgetsIndexed", widget.id);
        history.goBack();
        NotistackEnqueueSnackbar("Widget deleted successfully!", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error deleting the Widget.",
          { variant: "error" },
        );
        setConfirmDialogOpen(false);
        setIsProcessing(false);
      });
  };

  return (
    <LayerFunctional
      getMaxWidth={() => 1000}
      renderHeader={() => {
        const title = isUpdate ? "Edit Widget" : "Create Widget";
        const subtitle =
          isUpdate && widget?.id
            ? `${Widget.getDisplayName(widget)} • ${widget.id}`
            : null;

        const handleClickSave = () => {
          const nextValidationIssues = Validator.validate(widget, {
            name: {
              required: true,
            },
            widgetComponentTermId: {
              required: true,
            },
          });

          setValidationIssues(nextValidationIssues);

          if (Object.keys(nextValidationIssues).length === 0) {
            if (isUpdate) {
              updateWidget();
            } else {
              createWidget();
            }
          } else {
            NotistackEnqueueSnackbar("Please correct the displayed issues.", {
              variant: "error",
            });
          }
        };

        const buttons = [
          <MUIButton key="back" onClick={() => history.goBack()}>
            Back
          </MUIButton>,
          <MUILoadingButton
            key="save"
            loading={isProcessing}
            loadingPosition="start"
            onClick={handleClickSave}
            variant="contained"
            startIcon={<MUISaveButton />}
          >
            {isUpdate ? "Save" : "Create"}
          </MUILoadingButton>,
        ];

        if (isUpdate) {
          const menuItems = [
            <MUIMenuItem
              key="delete"
              onClick={() => setConfirmDialogOpen(true)}
            >
              <MUIListItemIcon>
                <MUIDeleteIcon fontSize="small" />
              </MUIListItemIcon>
              Delete
            </MUIMenuItem>,
          ];
          buttons.push(<Menu key="menu" menuItems={menuItems} />);
        }

        return (
          <PageHeader title={title} subtitle={subtitle} buttons={buttons} />
        );
      }}
      renderContent={() => (
        <>
          <CardAdminWidgetGeneral
            widget={widget}
            onChange={(changes) => {
              setWidget((previousWidget) => ({
                ...previousWidget,
                ...changes,
              }));
            }}
            validationIssues={validationIssues}
          />
          <DialogConfirm
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={() => deleteWidget()}
            text="Are you sure you want to delete this Widget? This cannot be undone."
            header="Delete Widget"
            loading={isProcessing}
          />
        </>
      )}
    />
  );
}
